import { useCallback } from 'react';
import { createHook, createSelector } from 'react-sweet-state';
import { UserTraitsStore } from '@atlassian/jira-business-common';
import type { UserTraitsState } from '@atlassian/jira-business-common/src/common/providers/controllers/traits';
import { useFlagsService } from '@atlassian/jira-flags';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { ISSUE_KEY_ID } from '../../common/constants';
import type { BoardIssue } from '../../common/types';
import { useIssueGroup } from '../issues-by-group';
import { useUpdateIssue } from '../update-issue';
import messages from './messages';

export const useCopyIssueLink = (issue: BoardIssue) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlagsService();

	return useCallback(async () => {
		const issueKey = issue.fields[ISSUE_KEY_ID].value;

		try {
			await navigator.clipboard.writeText(`${window.location.origin}/browse/${issueKey}`);

			showFlag({
				type: 'success',
				title: messages.copyLinkSuccessTitle,
				description: messages.copyLinkSuccessDescription,
			});

			fireTrackAnalytics(createAnalyticsEvent({ actionSubjectId: issue.id }), 'issue linkCopied', {
				throughActionCard: true,
				issueId: issue.id,
			});
		} catch (e) {
			// do nothing
		}
	}, [createAnalyticsEvent, issue, showFlag]);
};

type MoveIssueReturn = {
	canMoveToTop: boolean;
	canMoveToBottom: boolean;
	moveToTop: () => void;
	moveToBottom: () => void;
};

export const useMoveIssue = (issue: BoardIssue): MoveIssueReturn => {
	const { issues } = useIssueGroup();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const updateIssue = useUpdateIssue();
	const firstIssueId = issues[0]?.id;
	const canMoveToTop = firstIssueId !== issue.id;
	const lastIssueId = issues[issues.length - 1]?.id;
	const canMoveToBottom = lastIssueId !== issue.id;

	const moveToTop = useCallback(async () => {
		try {
			await updateIssue({
				issue,
				rank: { relativeIssueId: firstIssueId, position: 'before' },
			});

			fireTrackAnalytics(createAnalyticsEvent({ actionSubjectId: issue.id }), 'issue movedToTop', {
				throughActionCard: true,
				issueId: issue.id,
			});
		} catch (error: unknown) {
			// errors handled in updateIssue controller
		}
	}, [createAnalyticsEvent, firstIssueId, issue, updateIssue]);

	const moveToBottom = useCallback(async () => {
		try {
			await updateIssue({
				issue,
				rank: { relativeIssueId: lastIssueId, position: 'after' },
			});
			fireTrackAnalytics(
				createAnalyticsEvent({ actionSubjectId: issue.id }),
				'issue movedToBottom',
				{
					throughActionCard: true,
					issueId: issue.id,
				},
			);
		} catch (error: unknown) {
			// errors handled in updateIssue controller
		}
	}, [createAnalyticsEvent, issue, lastIssueId, updateIssue]);

	return {
		canMoveToTop,
		canMoveToBottom,
		moveToTop,
		moveToBottom,
	};
};

const isCardCoversNew = createSelector<
	UserTraitsState,
	undefined,
	boolean,
	UserTraitsState['attributes']
>(
	(state) => state.attributes,
	(attributes) => {
		if (!attributes) return false;

		const cardCoversReleaseDate = new Date('2024-06-17');

		const hasOpenedCardCoversEditor = attributes?.find(
			(trait) => trait?.name === 'jira-core_ui_cardcovereditor_clicked_first_time',
		);
		const accountCreationDate = attributes?.find(
			(trait) => trait?.name === 'jira_first_active',
		)?.value;

		let accountCreatedAfterCardCoversRelease = true;
		if (accountCreationDate && typeof accountCreationDate === 'string') {
			accountCreatedAfterCardCoversRelease = new Date(accountCreationDate) > cardCoversReleaseDate;
		}

		return !hasOpenedCardCoversEditor && !accountCreatedAfterCardCoversRelease;
	},
);

export const useIsCardCoversNew = createHook(UserTraitsStore, {
	selector: isCardCoversNew,
});
